/**
 * What the Admin Section may change on a page.
 *
 * The site's copy and its section order are fixed in the code fixtures. What an
 * editor may touch is a short, enumerated list — the Policy Map, the photograph
 * behind the hero, the photograph in the photo band, the coalition wall (which
 * partners, and whether the section runs at all), and the questions, answers
 * and resource links in a Q&A — plus the page's Visibility, which is a
 * publishing control rather than content. See docs/adr/0006, 0007, 0010 and
 * 0021.
 *
 * Every member but the last is a picture or a choice rather than a claim. The
 * Q&A is the deliberate exception: an FAQ answers questions the customer is
 * actually being asked, so it goes stale on their calendar and not on ours.
 * ADR-0010 records the argument.
 *
 * This module is the single statement of that rule, and it is imported by BOTH
 * the editor UI (which never renders the other fields) and the page write path
 * (which refuses them). Hiding it in the UI alone would be decorative: this
 * app's admin API is a route handler inside the OpenNext server Lambda, whose
 * Function URL is directly invokable, so CloudFront is not a boundary and
 * anything the API accepts is reachable with a hand-written request.
 *
 * Deliberately NOT "server-only" — the editor imports it too, and the whole
 * point is that both sides read the same list.
 */

import type {
  Block,
  FaqBlock,
  HeroBlock,
  LogoWallBlock,
  Page,
  PhotoBandBlock,
  PolicyMapBlock,
  PolicyMapTab,
  ResourceItem,
  UploadedImage,
} from "@/lib/content";

/** Block types an editor may modify. Everything else is fixed in the fixtures. */
export const EDITABLE_BLOCK_TYPES = ["policyMap", "photoBand", "logoWall", "hero", "faq"] as const;

type EditableBlockType = (typeof EDITABLE_BLOCK_TYPES)[number];

export type EditableBlock = Extract<Block, { type: EditableBlockType }>;

const EDITABLE = new Set<string>(EDITABLE_BLOCK_TYPES);

/**
 * The one shape an editor-supplied image may take. Mirrors `mediaSrc` in
 * lib/schema.ts and MEDIA_KEY in lib/media.ts — restated rather than imported
 * because this module is bundled into the editor, and lib/media.ts pulls the
 * AWS SDK. An <img> src is the reason it matters: anything wider would let a
 * hand-written request point every visitor at a stranger's server.
 */
const MEDIA_SRC = /^\/media\/[0-9a-f]{32}\.(webp|jpg|png)$/;

export function isEditableBlock(block: Block): block is EditableBlock {
  return EDITABLE.has(block.type);
}

/**
 * An image that came from the editor's own uploader, and not from anywhere else.
 *
 * The size is checked as well as the src because the consumer is `next/image`,
 * which THROWS on a missing width/height and would take the whole page down
 * with it rather than dropping one logo. On the write path zod has already
 * guaranteed both, so this only ever fires on a raw stored record.
 */
function isUpload(image: UploadedImage | undefined): image is UploadedImage {
  return (
    typeof image?.src === "string" &&
    MEDIA_SRC.test(image.src) &&
    typeof image.width === "number" &&
    image.width > 0 &&
    typeof image.height === "number" &&
    image.height > 0
  );
}

export function isEditableBlockType(type: Block["type"]): type is EditableBlockType {
  return EDITABLE.has(type);
}

/** True when a page has anything an editor can open. */
export function hasEditableBlock(page: Page): boolean {
  return page.blocks.some(isEditableBlock);
}

/**
 * Rebuild the page an editor is allowed to save: `base` (what they loaded)
 * with only the permitted parts taken from `incoming`.
 *
 * Reconstructing rather than diffing is what makes this safe. A field-by-field
 * comparison has to decide what "changed" means for nested objects, and JSON
 * round-tripping does not guarantee key order — so a legitimate save could be
 * rejected over a difference that isn't one. Here the server simply builds the
 * only page it is willing to write; nothing the caller sends can widen it.
 *
 * Block identity is by type, then position. Reordering, inserting and removing
 * are all gone, so a page carries the same sections on every save — but the
 * FIXTURE can still change under a record that was already stored, and pairing
 * on position alone breaks the moment it does.
 */
export function reconcilePage(base: Page, incoming: Page): Page {
  const offered = Array.isArray(incoming.blocks) ? incoming.blocks : [];
  const used = new Set<number>();

  // Take the next unconsumed incoming block of the SAME type. Matching on type
  // is what lets a record stored under the older, map-only allowlist resolve
  // against today's fixture: that record still carries the "Where we work" band
  // this release removed, so a position-only walk would hand the map's saved
  // state to the photo band. Same-type blocks are still paired in order.
  const take = (type: Block["type"]): Block | undefined => {
    for (let i = 0; i < offered.length; i += 1) {
      if (used.has(i) || offered[i]?.type !== type) continue;
      used.add(i);
      return offered[i];
    }
    return undefined;
  };

  const blocks = base.blocks.map((block) =>
    isEditableBlock(block) ? reconcileBlock(block, take(block.type)) : block,
  );

  return {
    ...base,
    // Visibility is a publishing control, not site copy — and since ADR-0011
    // took "Take offline" out of the admin, it is the ONLY way a customer takes
    // a page off the public site. Locking it would strand a hidden page.
    status: incoming.status,
    blocks,
  };
}

/** Dispatch one editable block to the rule for its type. */
function reconcileBlock(base: EditableBlock, incoming: Block | undefined): Block {
  switch (base.type) {
    case "policyMap":
      return reconcilePolicyMap(base, incoming);
    case "photoBand":
      return reconcilePhotoBand(base, incoming);
    case "logoWall":
      return reconcileLogoWall(base, incoming);
    case "hero":
      return reconcileHero(base, incoming);
    case "faq":
      return reconcileFaq(base, incoming);
  }
}

/**
 * Rebuild the Q&A: `base` in full, except the questions, the answers and the
 * Resource Rows under each answer — which the editor owns outright, including
 * how many there are and what order they run in.
 *
 * The heading above them ("Commonly Asked Questions & Resources") stays Fixed
 * Copy for the usual reason: it frames the section, and it is the same on every
 * page that carries one. See docs/adr/0010 and docs/adr/0021.
 *
 * A Resource Row's href lands in an `<a>` on a public page, so each one is
 * rebuilt field by field and kept only if its href is one the answer's own
 * links could carry (RESOURCE_HREF). This runs on read as well as on write, and
 * the read path never sees pageSchema, so it is the only check a stored row
 * meets on its way to a visitor.
 */
function reconcileFaq(base: FaqBlock, incoming: Block | undefined): FaqBlock {
  if (incoming?.type !== "faq" || !Array.isArray(incoming.items)) return base;
  return {
    ...base,
    items: incoming.items.map((item) => {
      const offered = item.resources;
      return {
        question: item.question,
        answerHtml: item.answerHtml,
        // Present exactly when it was offered, and never invented: zod omits an
        // optional key it never saw, so adding `resources: []` to a question
        // that had none would differ from its own re-parse and report every
        // honest save as an attempt. The ADR-0004 trap again.
        ...(Array.isArray(offered) ? { resources: offered.flatMap(reconcileResource) } : {}),
      };
    }),
  };
}

/**
 * What a Resource Row's href may be: a page or Uploaded Document on this site,
 * an anchor, an http(s) address, mailto: or tel: — the set `sanitizeRichText`
 * allows in an answer, so a row can link nothing the paragraph above it could
 * not. A bare "#" is a row with no link yet. The public page hides those
 * (components/blocks/FaqItem.tsx) and the editor flags them.
 *
 * Narrower than `safeHref`, which waves through anything starting with "/".
 * A browser reads `//host`, `/\host` and `/<tab>/host` all as another site, so
 * the test runs the way sanitize-html's does: whitespace and control characters
 * out first (browsers drop them from a URL), then no second slash or backslash.
 */
const RESOURCE_HREF = /^(\/(?![\/\\])|#|https?:\/\/|mailto:|tel:)/i;

/** Whether `href` may be a Resource Row's link. The editor asks before a save. */
export function resourceHrefAllowed(href: string): boolean {
  return RESOURCE_HREF.test(href.replace(/[\x00-\x20]/g, ""));
}

/** The href of a Resource Row that has no link yet. */
export const RESOURCE_NO_LINK = "#";

/** Whether a Resource Row goes anywhere, and so whether a visitor sees it. */
export function resourceHasLink(row: Pick<ResourceItem, "href">): boolean {
  return row.href !== RESOURCE_NO_LINK;
}

/** One Resource Row, rebuilt from the fields the model declares, or nothing. */
function reconcileResource(offered: unknown): ResourceItem[] {
  if (!offered || typeof offered !== "object") return [];
  const { label, href, kind, source } = offered as Record<string, unknown>;
  if (typeof label !== "string" || !label.trim()) return [];
  if (typeof href !== "string" || !resourceHrefAllowed(href)) return [];
  if (kind !== "link" && kind !== "pdf") return [];
  return [{ label, href, kind, ...(typeof source === "string" ? { source } : {}) }];
}

/**
 * Rebuild the hero: `base` in full, except the photograph behind it.
 *
 * The headline, the accent phrase, the lead, the buttons and the brief card are
 * the site's argument for itself — the most load-bearing copy on the site, and
 * squarely Fixed Copy. The background is the one thing here that is a picture
 * rather than a claim.
 *
 * Unlike the photo band, absent is a legitimate state: no background means the
 * hero renders on plain paper, which is the shipped default. So clearing it is
 * allowed, and the key is dropped rather than set to undefined — zod omits an
 * optional key it never saw, and the record has to round-trip its own re-parse.
 */
function reconcileHero(base: HeroBlock, incoming: Block | undefined): HeroBlock {
  if (incoming?.type !== "hero") return base;
  const { background: _cleared, ...rest } = base;
  return isUpload(incoming.background) ? { ...rest, background: incoming.background } : rest;
}

/**
 * Rebuild the photo band: `base` in full, except the photograph itself.
 *
 * The alt text, caption and aspect ratio are editorial copy authored in
 * content/pages.ts — the band is a fixed piece of art direction whose subject
 * happens to be swappable. Only a `/media/` upload is accepted: `imageSlot.src`
 * is validated by `safeHref`, which allows any https host, and that is the right
 * rule for a fixture path but not for a value a request can supply.
 */
function reconcilePhotoBand(base: PhotoBandBlock, incoming: Block | undefined): PhotoBandBlock {
  if (incoming?.type !== "photoBand" || !base.image || !incoming.image) return base;
  const { src, width, height } = incoming.image;
  if (typeof src !== "string" || !MEDIA_SRC.test(src)) return base;
  return { ...base, image: { ...base.image, src, width, height } };
}

/**
 * Rebuild the coalition wall: `base` in full, except which partners are on it
 * and whether it runs at all.
 *
 * The eyebrow, heading, intro and note are site copy. `hidden` is passed
 * through exactly as offered rather than normalised to a boolean: sortKeys
 * drops `undefined` but keeps `false`, so coercing an absent flag to `false`
 * here would make attemptedForbiddenChange report every honest save from a
 * client that omits it. That is the ADR-0004 trap, and it is one line wide.
 */
function reconcileLogoWall(base: LogoWallBlock, incoming: Block | undefined): LogoWallBlock {
  if (incoming?.type !== "logoWall" || !Array.isArray(incoming.logos)) return base;
  return {
    ...base,
    hidden: incoming.hidden,
    logos: incoming.logos.map((logo) => ({
      name: logo.name,
      // Absent rather than `undefined`, so the record round-trips its own
      // re-parse: zod omits an optional key it never saw.
      //
      // The /media/ check is not redundant with the schema. On the WRITE path
      // `incoming` has been through `uploadedImage`, so this never fires; on the
      // READ path it is the raw stored record, which resolvePage feeds straight
      // here without a re-parse. Without it, "an Editable Block only ever points
      // at Uploaded Media" would hold for the photo band and not for these.
      ...(isUpload(logo.image) ? { image: logo.image } : {}),
    })),
  };
}

/**
 * Rebuild one map: `base` in full, except the two things that are genuinely the
 * customer's — which states carry which category, and whether a view is
 * published at all.
 *
 * The map's framing (eyebrow, heading, intro, legend wording, footnote, each
 * view's intro) and its STRUCTURE (how many views, how many categories, their
 * ids AND their labels) are site copy, authored in content/pages.ts. Renaming a
 * view or a category is a code change: ADR-0005 shipped with the labels
 * editable, recorded that locking them was one line, and that line was asked
 * for on 2026-07-23. Do not restore them here without restoring the form field
 * too — a lock in one place and not the other reads as a bug in whichever half
 * is found second.
 *
 * `hidden` joined the list on 2026-07-31. It is not an exception to "structure
 * is site copy": how many views exist is still fixed here, and hiding one no
 * more removes it than taking the coalition wall offline deletes its logos.
 *
 * Views match positionally for the same reason blocks do: adding, removing and
 * reordering a view are all gone, so index `i` is the same view on every save.
 * Id-matching would be worse — tab ids are not checked for uniqueness within a
 * block, so an id-keyed merge could apply one view's selections to two views.
 */
function reconcilePolicyMap(base: PolicyMapBlock, incoming: Block | undefined): PolicyMapBlock {
  // `base` is NOT always schema-valid: savePageDraft deliberately passes a raw
  // stored record through when it fails to parse, because wedging the editor on
  // a record we could not read is the worse failure (ADR-0004). So never assume
  // a shape here — degrade to "write base back" rather than throwing.
  if (incoming?.type !== "policyMap" || !Array.isArray(base.tabs)) return base;
  return { ...base, tabs: base.tabs.map((tab, i) => reconcileTab(tab, incoming.tabs?.[i])) };
}

function reconcileTab(base: PolicyMapTab, incoming: PolicyMapTab | undefined): PolicyMapTab {
  if (!incoming || !Array.isArray(base.categories) || !Array.isArray(incoming.states)) return base;

  // The categories come from `base` untouched — id AND label. A view's name and
  // its category names are part of the research design, authored in
  // content/pages.ts, so the only thing an editor changes here is which states
  // carry which category.
  //
  // ADR-0005 originally left the labels editable and said locking them was one
  // line. This is that line, asked for on 2026-07-23. The consequence is the one
  // recorded there: renaming a category is now a code change, and because
  // resolvePage reads through this same function, any rename already stored is
  // dropped on the next read rather than lingering.
  const categories = base.categories;

  // pageSchema validates the payload's selections against the PAYLOAD's own
  // category list, so a hand-written request can declare a category, tick it,
  // and pass validation. Dropping selections we have no category for is what
  // stops that writing a dangling id — a record that would then fail its own
  // re-parse forever, taking the 403 report down with it.
  const known = new Set(categories.map((c) => c.id));

  // Whether a view is published is a control over the customer's own data, not
  // site copy — the same call logoWall's `hidden` makes.
  //
  // Two rules, and both are load-bearing. It is passed through exactly as
  // offered and NEVER coerced: sortKeys drops `undefined` but keeps `false`, so
  // `incoming.hidden ?? false` would make attemptedForbiddenChange report every
  // honest save from a client that omits it — the ADR-0004 trap, one line wide.
  // And the key is DROPPED rather than set to `undefined`, the way reconcileHero
  // clears a background: zod omits an optional key it never saw, so the record
  // has to round-trip its own re-parse. Stripping it from `base` first is what
  // makes un-hiding work at all — otherwise a stored `true` would outlive every
  // payload that omits the key, and the switch would only go one way.
  const { hidden: _cleared, ...withoutFlag } = base;
  return {
    ...withoutFlag,
    ...(incoming.hidden === undefined ? {} : { hidden: incoming.hidden }),
    // Deliberately NOT sorted, de-duplicated or pruned. The editor rebuilds this
    // array from a fixed list of 51, so its order legitimately differs from the
    // stored one on every save; normalising one side of attemptedForbiddenChange
    // is precisely the ADR-0004 trap and would 403 every honest save, forever.
    states: incoming.states.map((entry) => ({
      code: entry.code,
      selections: entry.selections.filter((s) => known.has(s.categoryId)),
    })),
  };
}

/**
 * Resolve a stored record against the fixture the way the WRITE path does.
 *
 * The stored record is an OVERLAY, not a replacement page. Reading it through
 * the same allowlist means a deploy that fixes a headline, adds a view or
 * corrects the map's footnote lands immediately on a page the customer already
 * saved, instead of needing a revert to the fixture — which throws away every
 * state assignment they have entered, and since ADR-0011 is not something the
 * customer can even do. Without this, "the map's copy is site
 * copy" stops being true the first time the tool is used.
 *
 * `updatedAt` comes from the override: it drives the sitemap's lastmod and the
 * admin list's sort, and the fixture's is a build-time constant.
 */
export function resolvePage(fixture: Page, stored: Page): Page {
  return { ...reconcilePage(fixture, stored), updatedAt: stored.updatedAt };
}

/**
 * Whether `incoming` tried to change anything it isn't allowed to. Used only to
 * report the attempt honestly — the write itself uses reconcilePage's result
 * either way, so a false negative here can never widen what gets stored.
 */
export function attemptedForbiddenChange(base: Page, incoming: Page): boolean {
  // `updatedAt` is stamped server-side on every write, so a client sending a
  // stale or invented one is not an attempt to edit anything — comparing it
  // would just produce a spurious refusal.
  const ignoreTimestamp = (p: Page) => ({ ...p, updatedAt: "" });
  return (
    stable(ignoreTimestamp(reconcilePage(base, incoming))) !==
    stable(ignoreTimestamp(incoming))
  );
}

/** JSON with object keys sorted, so comparison doesn't depend on key order. */
function stable(value: unknown): string {
  return JSON.stringify(sortKeys(value));
}

function sortKeys(value: unknown): unknown {
  if (Array.isArray(value)) return value.map(sortKeys);
  if (value && typeof value === "object") {
    return Object.fromEntries(
      Object.entries(value as Record<string, unknown>)
        .filter(([, v]) => v !== undefined)
        .sort(([a], [b]) => a.localeCompare(b))
        .map(([k, v]) => [k, sortKeys(v)]),
    );
  }
  return value;
}
